import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

const SAMPLE_REPORTS = [
  {
    id: 1,
    label: 'Executive A',
    type: 'Full Intelligence Report',
    price: '$2,500',
    pages: '47 pages',
    filename: 'ANONYMIZED_1_Sample_Report.pdf',
    highlights: ['42 data brokers found', '6 breach exposures', 'Aircraft tail tracked', 'Family network mapped'],
    risk: 82,
    tag: 'CRITICAL',
    tagColor: 'text-red-400 border-red-500/40 bg-red-950/30',
  },
  {
    id: 2,
    label: 'Executive B',
    type: 'Full Intelligence Report',
    price: '$2,500',
    pages: '39 pages',
    filename: 'ANONYMIZED_2_Sample_Report.pdf',
    highlights: ['28 data brokers found', '3 breach exposures', 'Home address exposed', 'Dark web credential sale'],
    risk: 67,
    tag: 'SEVERE',
    tagColor: 'text-orange-400 border-orange-500/40 bg-orange-950/30',
  },
  {
    id: 3,
    label: 'Executive C',
    type: 'Quick Collection Report',
    price: '$295',
    pages: '18 pages',
    filename: 'ANONYMIZED_3_Sample_Report.pdf',
    highlights: ['19 data brokers found', '2 breach exposures', 'Opt-out links included', 'Remediation checklist'],
    risk: 51,
    tag: 'ELEVATED',
    tagColor: 'text-yellow-400 border-yellow-500/40 bg-yellow-950/30',
  },
  {
    id: 4,
    label: 'Executive D',
    type: 'Full Intelligence Report + CI',
    price: '$2,500',
    pages: '52 pages',
    filename: 'ANONYMIZED_4_Sample_Report.pdf',
    highlights: ['38 brokers found', '9 breach exposures', 'Counterintel assessment', '4D risk triage'],
    risk: 74,
    tag: 'SEVERE',
    tagColor: 'text-orange-400 border-orange-500/40 bg-orange-950/30',
  },
]

export default function SampleReport() {
  const [selected, setSelected] = useState(SAMPLE_REPORTS[0])
  const [pdfLoaded, setPdfLoaded] = useState(false)

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at top, #0f0800 0%, #080810 60%)' }}>
      <Navbar />

      <div className="pt-20 pb-16 px-4">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10 mt-6"
          >
            <div className="inline-flex items-center gap-2 bg-ginger-500/10 border border-ginger-500/30 rounded-full px-4 py-1.5 mb-4">
              <div className="w-1.5 h-1.5 rounded-full bg-ginger-500 animate-pulse" />
              <span className="text-ginger-400 text-xs font-bold tracking-widest uppercase">
                Sample Report — Anonymized Executive
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-3">
              See What a{' '}
              <span className="text-ginger-500">$2,500 Intelligence Report</span>{' '}
              Looks Like
            </h1>
            <p className="text-dark-400 max-w-2xl mx-auto">
              Real reports. Real findings. Names redacted.
              This is what our analysts produce — 40+ pages of intelligence-grade exposure analysis.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-[320px_1fr] gap-6">

            {/* Left: Report selector */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="space-y-3"
            >
              <h2 className="text-xs font-mono font-bold text-dark-500 uppercase tracking-widest mb-3">
                Select a Sample Report
              </h2>

              {SAMPLE_REPORTS.map(report => (
                <button
                  key={report.id}
                  onClick={() => { setSelected(report); setPdfLoaded(false) }}
                  className={`w-full text-left rounded-xl border p-4 transition-all ${
                    selected.id === report.id
                      ? 'border-ginger-500/60 bg-ginger-950/30'
                      : 'border-dark-700/50 bg-dark-900 hover:border-dark-600'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-bold text-sm text-dark-100">{report.label}</div>
                      <div className="text-xs text-dark-400">{report.type}</div>
                    </div>
                    <div className={`text-xs font-bold px-2 py-0.5 rounded border ${report.tagColor}`}>
                      {report.tag}
                    </div>
                  </div>

                  {/* Risk bar */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-dark-500 mb-1">
                      <span>Risk Score</span>
                      <span className={
                        report.risk >= 70 ? 'text-red-400' :
                        report.risk >= 50 ? 'text-orange-400' : 'text-yellow-400'
                      }>{report.risk}/100</span>
                    </div>
                    <div className="w-full h-1.5 bg-dark-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${report.risk}%`,
                          background: report.risk >= 70 ? '#ef4444' : report.risk >= 50 ? '#f97316' : '#eab308',
                        }}
                      />
                    </div>
                  </div>

                  <ul className="space-y-1">
                    {report.highlights.map(h => (
                      <li key={h} className="flex items-center gap-2 text-xs text-dark-400">
                        <span className="text-ginger-500 font-bold">·</span>
                        {h}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-dark-800">
                    <span className="text-xs text-dark-500">{report.pages}</span>
                    <span className="text-xs font-semibold text-ginger-400">{report.price}</span>
                  </div>
                </button>
              ))}

              {/* CTA */}
              <div className="bg-dark-900 border border-dark-700/50 rounded-xl p-4 mt-4">
                <p className="text-dark-400 text-xs mb-3">
                  Ready to see your own exposure?
                </p>
                <Link to="/" className="btn btn-primary w-full text-sm">
                  Get Your Free Snapshot
                </Link>
                <Link to="/checkout/full" className="btn btn-secondary w-full text-sm mt-2">
                  Order Executive Report
                </Link>
              </div>
            </motion.div>

            {/* Right: PDF viewer */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="flex flex-col"
            >
              {/* Report header */}
              <div className="bg-dark-900 border border-dark-700/50 rounded-xl rounded-b-none p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-dark-100">{selected.label} — {selected.type}</div>
                  <div className="text-xs text-dark-500 font-mono mt-0.5">
                    Gingerbread! Executive Privacy &nbsp;·&nbsp; {selected.pages} &nbsp;·&nbsp; Anonymized for privacy
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-3 py-1 rounded border ${selected.tagColor}`}>
                    Risk: {selected.risk}/100
                  </span>
                  <a
                    href={`/reports/${selected.filename}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-secondary text-xs py-1.5 px-3"
                  >
                    Open PDF ↗
                  </a>
                </div>
              </div>

              {/* PDF iframe */}
              <div className="relative bg-dark-950 border border-dark-700/50 border-t-0 rounded-b-xl overflow-hidden"
                style={{ height: '80vh', minHeight: '600px' }}>

                {!pdfLoaded && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-dark-950">
                    <div className="w-10 h-10 border-2 border-ginger-500 border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-dark-500 font-mono text-sm">Loading report...</p>
                  </div>
                )}

                <iframe
                  key={selected.filename}
                  src={`/reports/${selected.filename}#toolbar=0&navpanes=0&scrollbar=1`}
                  className="w-full h-full border-0"
                  onLoad={() => setPdfLoaded(true)}
                  title={`Sample Report — ${selected.label}`}
                />
              </div>

              {/* Bottom trust bar */}
              <div className="mt-4 grid grid-cols-4 gap-3">
                {[
                  { label: '1,008+ Sources', sub: 'scanned per report' },
                  { label: '38 Threat Signals', sub: 'classified per finding' },
                  { label: 'ICD-203 Protocol', sub: 'intelligence standard' },
                  { label: 'Human Analyst', sub: 'validates every report' },
                ].map(({ label, sub }) => (
                  <div key={label} className="bg-dark-900 border border-dark-700/50 rounded-lg p-3 text-center">
                    <div className="text-sm font-bold text-ginger-400">{label}</div>
                    <div className="text-xs text-dark-500 mt-0.5">{sub}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
